import { Autopilot } from '../sim/autopilot';
import { STEP_MS } from '../sim/constants';
import { Sim } from '../sim/sim';
import type { SimConfig } from '../sim/types';
import type { RunTicket } from '../net/api';

const MAX_CATCHUP_TICKS = 30;

/**
 * Runs a simulation in real time. Inputs keep their exact timestamps and are
 * applied at the tick they belong to, not at the next animation frame.
 */
export class Session {
  readonly sim: Sim;
  readonly ticket: RunTicket | null;
  private startPerf: number;
  private queue: { ts: number; kind: 0 | 1 }[] = [];
  private pausedAt = -1;
  private bot: Autopilot | null;

  constructor(cfg: SimConfig, ticket: RunTicket | null, now: number, demo = false) {
    this.sim = new Sim(cfg);
    this.ticket = ticket;
    this.startPerf = now;
    this.bot = demo ? new Autopilot(true) : null;
  }

  get paused(): boolean {
    return this.pausedAt >= 0;
  }

  push(ts: number, kind: 0 | 1): void {
    if (this.paused || this.bot) return;
    this.queue.push({ ts, kind });
  }

  /** Steps the simulation up to `now`. Returns the fraction of a tick left over. */
  advance(now: number): number {
    if (this.paused) return 0;
    const sim = this.sim;
    const target = Math.floor((now - this.startPerf) / STEP_MS);
    let n = 0;
    while (sim.tick < target && !sim.over) {
      while (this.queue.length && this.tickOf(this.queue[0].ts) <= sim.tick) {
        sim.input(this.queue.shift()!.kind);
      }
      this.bot?.act(sim);
      sim.step();
      if (++n >= MAX_CATCHUP_TICKS) {
        // The tab stalled: slow time down instead of jumping ahead.
        this.startPerf = now - sim.tick * STEP_MS;
        break;
      }
    }
    if (sim.over) {
      this.queue.length = 0;
      return 0;
    }
    return Math.min(1, Math.max(0, (now - this.startPerf) / STEP_MS - sim.tick));
  }

  private tickOf(ts: number): number {
    return Math.floor((ts - this.startPerf) / STEP_MS);
  }

  pause(now: number): void {
    if (!this.paused) this.pausedAt = now;
  }

  resume(now: number): void {
    if (!this.paused) return;
    this.startPerf += now - this.pausedAt;
    this.pausedAt = -1;
  }
}
